import { useState, useEffect } from "react"
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"
import { Rocket, Check } from "lucide-react"

const CYCLE_DURATION = 8000

const MILESTONES = [
  { day: 7, label: "Wireframes" },
  { day: 14, label: "Auth + DB" },
  { day: 21, label: "Payments" },
  { day: 28, label: "Deploy" },
]

const LANES = [
  { label: "Design", startDay: 1, endDay: 7 },
  { label: "Build", startDay: 8, endDay: 21 },
  { label: "Launch", startDay: 22, endDay: 30 },
]

function WireframeEmpty() {
  return (
    <div className="w-full h-full grid grid-cols-3 gap-1.5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-slate-200 rounded" />
      ))}
    </div>
  )
}

function WireframeFilled() {
  return (
    <div className="w-full h-full flex gap-2">
      <div className="flex-1 space-y-1.5">
        <div className="h-2.5 bg-slate-300 rounded w-3/4" />
        <div className="h-2 bg-slate-200 rounded w-full" />
        <div className="h-2 bg-slate-200 rounded w-5/6" />
        <div className="mt-2 flex-1 bg-slate-200 rounded flex items-center justify-center min-h-[60px]">
          <svg className="w-10 h-8 text-slate-300" viewBox="0 0 48 40">
            <polyline points="4,30 12,18 24,24 36,12 44,20" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </div>
      </div>
      <div className="w-16 sm:w-20 space-y-1.5">
        <div className="h-6 bg-slate-200 rounded" />
        <div className="h-6 bg-slate-200 rounded" />
        <div className="h-6 bg-slate-200 rounded" />
      </div>
    </div>
  )
}

function Confetti() {
  const dots = Array.from({ length: 8 }).map((_, i) => ({
    x: Math.random() * 40 - 20,
    y: Math.random() * -30 - 10,
    delay: Math.random() * 0.3,
    color: i % 3 === 0 ? "#ed2132" : i % 3 === 1 ? "#fecaca" : "#cbd5e1",
  }))

  return (
    <div className="absolute top-4 right-4 pointer-events-none">
      {dots.map((dot, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{ backgroundColor: dot.color }}
          initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 1, 0], x: dot.x, y: dot.y, scale: [0, 1, 1, 0.5] }}
          transition={{ duration: 1, delay: dot.delay, repeat: Infinity, repeatDelay: 7 }}
        />
      ))}
    </div>
  )
}

function PolishedUI({ showLive = false }: { showLive?: boolean }) {
  return (
    <div className="w-full h-full flex gap-2 relative">
      {showLive && <div className="absolute -top-2 -right-2 w-16 h-16 bg-red-100/40 rounded-full blur-xl" />}
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full bg-slate-300" />
          <div className="h-2.5 bg-slate-800 rounded w-16" />
        </div>
        <div className="h-12 bg-white border border-slate-200 rounded p-1.5">
          <svg className="w-full h-full" viewBox="0 0 120 32">
            <defs>
              <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ed2132" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#ed2132" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M 0 24 L 20 20 L 40 22 L 60 14 L 80 16 L 100 8 L 120 4" fill="none" stroke="#ed2132" strokeWidth="2" />
            <path d="M 0 24 L 20 20 L 40 22 L 60 14 L 80 16 L 100 8 L 120 4 L 120 32 L 0 32 Z" fill="url(#sparklineGradient)" />
          </svg>
        </div>
        <div className="flex gap-1.5">
          <div className="h-5 bg-[#ed2132] rounded px-2 flex items-center">
            <span className="text-[8px] text-white font-medium">Get Started</span>
          </div>
          <div className="h-5 bg-slate-200 rounded px-2 flex items-center">
            <span className="text-[8px] text-slate-600 font-medium">Learn More</span>
          </div>
        </div>
      </div>
      <div className="w-20 sm:w-24 bg-white border border-slate-200 rounded overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-1.5 py-0.5">
          <span className="text-[7px] font-medium text-slate-600">Users</span>
        </div>
        <div className="divide-y divide-slate-100">
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-1.5 py-1 flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
              <div className="h-1 bg-slate-200 rounded flex-1" />
            </div>
          ))}
        </div>
      </div>
      {showLive && (
        <>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute top-0 right-0 bg-green-100 border border-green-300 rounded-full px-1.5 py-0.5 flex items-center gap-0.5"
          >
            <div className="w-1 h-1 rounded-full bg-green-500" />
            <span className="text-[7px] font-semibold text-green-700">Live</span>
          </motion.div>
          <Confetti />
        </>
      )}
    </div>
  )
}

function BrowserWindow({ stage }: { stage: number }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-slate-300" />
          <div className="w-2 h-2 rounded-full bg-slate-300" />
          <div className="w-2 h-2 rounded-full bg-slate-300" />
        </div>
        <div className="flex-1 flex justify-center">
          <AnimatePresence mode="wait">
            {stage < 3 ? (
              <motion.div key="url" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-white border border-slate-200 rounded px-2 py-0.5 text-[10px] text-slate-500">
                yourapp.com
              </motion.div>
            ) : (
              <motion.div key="live" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1 bg-white border border-slate-200 rounded px-2 py-0.5 text-[10px] text-slate-600">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Live
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="w-12" />
      </div>
      <div className="p-3 h-[160px] sm:h-[180px] relative">
        <AnimatePresence mode="wait">
          {stage === 0 && (
            <motion.div key="wireframe-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-3">
              <WireframeEmpty />
            </motion.div>
          )}
          {stage === 1 && (
            <motion.div key="wireframe-filled" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-3">
              <WireframeFilled />
            </motion.div>
          )}
          {stage === 2 && (
            <motion.div key="polished" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-3">
              <PolishedUI />
            </motion.div>
          )}
          {stage === 3 && (
            <motion.div key="polished-live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-3">
              <PolishedUI showLive />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="absolute top-1.5 left-1.5 bg-white/90 border border-slate-200 rounded px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
          {stage === 0 ? "Week 1" : stage === 1 ? "Week 2" : stage === 2 ? "Week 3" : "Week 4"}
        </div>
      </div>
    </div>
  )
}

function GanttTimeline({ currentDay }: { currentDay: number }) {
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {LANES.map((lane, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 w-12 text-right shrink-0">{lane.label}</span>
            <div className="flex-1 h-4 bg-slate-100 rounded-sm relative overflow-hidden">
              <motion.div
                className={`absolute top-0 bottom-0 rounded-sm origin-left ${i === 2 ? "bg-[#ed2132]" : i === 1 ? "bg-red-200" : "bg-red-300"}`}
                style={{ left: `${((lane.startDay - 1) / 30) * 100}%`, width: `${((lane.endDay - lane.startDay + 1) / 30) * 100}%` }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: currentDay >= lane.startDay ? Math.min(1, (currentDay - lane.startDay + 1) / (lane.endDay - lane.startDay + 1)) : 0 }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="text-center">
        <span className="text-xs font-semibold text-[#ed2132]">Day {currentDay} of 30</span>
      </div>
    </div>
  )
}

function MilestonePill({ day, label, isComplete }: { day: number; label: string; isComplete: boolean }) {
  return (
    <motion.div
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium transition-colors shrink-0 ${isComplete ? "bg-white border border-slate-200 text-slate-900 shadow-sm" : "bg-slate-100 border border-slate-200 text-slate-500"}`}
      animate={{ scale: isComplete ? [1, 1.05, 1] : 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className={`w-3 h-3 rounded-full flex items-center justify-center ${isComplete ? "bg-[#ed2132]" : "bg-slate-200"}`}>
        {isComplete && <Check className="w-2 h-2 text-white" strokeWidth={3} />}
      </div>
      <span className="whitespace-nowrap">{label}</span>
    </motion.div>
  )
}

export function HeroBuildTracker() {
  const prefersReducedMotion = useReducedMotion()
  const [currentDay, setCurrentDay] = useState(prefersReducedMotion ? 30 : 1)
  const [stage, setStage] = useState(prefersReducedMotion ? 3 : 0)

  useEffect(() => {
    if (prefersReducedMotion) return
    const interval = setInterval(() => {
      setCurrentDay((prev) => {
        const next = prev >= 30 ? 1 : prev + 1
        if (next <= 7) setStage(0)
        else if (next <= 14) setStage(1)
        else if (next <= 24) setStage(2)
        else setStage(3)
        return next
      })
    }, CYCLE_DURATION / 30)
    return () => clearInterval(interval)
  }, [prefersReducedMotion])

  return (
    <div className="w-full max-w-md lg:max-w-lg" role="img" aria-label="Animated 30-day MVP build tracker showing weekly milestones from wireframes to production launch">
      <div className="space-y-4">
        <BrowserWindow stage={stage} />
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {MILESTONES.map((milestone) => (
            <MilestonePill key={milestone.day} day={milestone.day} label={milestone.label} isComplete={currentDay >= milestone.day} />
          ))}
        </div>
        <GanttTimeline currentDay={currentDay} />
        <div className="flex justify-center pt-2">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1.5">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-red-100">
              <Rocket className="h-3 w-3 text-[#ed2132]" />
            </div>
            <span className="text-xs font-medium text-slate-700">30 days · Fixed price</span>
          </div>
        </div>
      </div>
    </div>
  )
}
